import { addSessionEvent } from '@/db/session-events';
import type { PlayerId, RoundId, SessionId, UserId } from '@/schemas';
import { createOperationId } from '../../operation-queue';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type ScoreDeltaQueueParams = QueueDependencies & {
  sessionId: SessionId;
  roundId: RoundId;
  playerId: PlayerId;
  delta: number;
  userId: UserId | undefined;
};

type ScoreSetQueueParams = QueueDependencies & {
  sessionId: SessionId;
  roundId: RoundId;
  playerId: PlayerId;
  value: number;
  userId: UserId | undefined;
  hasExistingScore: boolean;
  previousScore: number | undefined;
};

export const enqueueScoreDelta = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  roundId,
  playerId,
  delta,
  userId,
}: ScoreDeltaQueueParams): Promise<void> => {
  const operationId = createOperationId();
  const dedupKey = `score:add:${sessionId}:${roundId}:${playerId}`;

  const optimisticUpdate = () => {
    set((draft) => {
      const round = draft.activeSession?.rounds.find((r) => r.id === roundId);
      if (round) {
        const currentScore = round.scores[playerId] ?? 0;
        round.scores[playerId] = currentScore + delta;
      }
    });
  };

  const rollback = async () => {
    set((draft) => {
      const round = draft.activeSession?.rounds.find((r) => r.id === roundId);
      if (round) {
        const currentScore = round.scores[playerId] ?? 0;
        const nextValue = currentScore - delta;
        if (nextValue === 0) {
          delete round.scores[playerId];
        } else {
          round.scores[playerId] = nextValue;
        }
      }
    });
    await refreshSessionFromEvents(sessionId);
  };

  const execute = async () => {
    await addSessionEvent(
      createSystemEvent(
        sessionId,
        'ScoreChanged',
        {
          roundId,
          playerId,
          delta,
        },
        userId,
      ),
    );
    await updateUndoRedoAvailability(sessionId);
  };

  await operationQueue.enqueue<void>({
    id: operationId,
    type: 'addScore',
    dedupKey,
    priority: 10,
    optimisticUpdate,
    rollback,
    execute,
  });
};

export const enqueueScoreSet = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  roundId,
  playerId,
  value,
  userId,
  hasExistingScore,
  previousScore,
}: ScoreSetQueueParams): Promise<void> => {
  const operationId = createOperationId();
  const dedupKey = `score:set:${sessionId}:${roundId}:${playerId}`;

  const optimisticUpdate = () => {
    set((draft) => {
      const round = draft.activeSession?.rounds.find((r) => r.id === roundId);
      if (round) {
        round.scores[playerId] = value;
      }
    });
  };

  const rollback = async () => {
    set((draft) => {
      const round = draft.activeSession?.rounds.find((r) => r.id === roundId);
      if (round) {
        if (hasExistingScore) {
          if (previousScore === undefined) {
            delete round.scores[playerId];
          } else {
            round.scores[playerId] = previousScore;
          }
        } else {
          delete round.scores[playerId];
        }
      }
    });
    await refreshSessionFromEvents(sessionId);
  };

  const execute = async () => {
    await addSessionEvent(
      createSystemEvent(
        sessionId,
        'ScoreSet',
        {
          roundId,
          playerId,
          value,
        },
        userId,
      ),
    );
    await updateUndoRedoAvailability(sessionId);
  };

  await operationQueue.enqueue<void>({
    id: operationId,
    type: 'setScore',
    dedupKey,
    priority: 9,
    optimisticUpdate,
    rollback,
    execute,
  });
};
